/**
 * cloneStatusConfig.js
 *
 * Single source of truth for VoiceClone lifecycle states shown on the
 * Book Creator's Voice Cloning Pipeline page.
 *
 * IMPORTANT: The keys here MUST mirror VoiceCloningService.CLONE_STATUS
 * on the server. The server is the authority for state transitions; this
 * config only decorates each state with presentation metadata (label,
 * color, icon, description, terminal flag).
 *
 * Server state machine (see server/services/voiceCloning/VoiceCloningService.js):
 *
 *   queued         Seeded — waiting for the export worker to pick it up
 *   exporting      Worker is uploading source WAV → ElevenLabs /voices/add
 *   exported       ElevenLabs accepted the IVC request (IVC is synchronous)
 *   elabs_in_process  PVC only — ElevenLabs is fine-tuning the voice
 *   elabs_completed   ElevenLabs reports the voice is ready; smoke test pending
 *   elabs_failed      ElevenLabs rejected or failed training
 *   smoke_testing  TTS call in flight to produce the smoke-test audio
 *   success        Smoke-test audio uploaded to S3; clone is ready to use
 *   failed         Any terminal local or remote failure (see errorMessage)
 *   deleting       User clicked delete; awaiting ElevenLabs DELETE
 *
 * Plus one client-only virtual state:
 *
 *   none           Sample is approved but no VoiceClone row has been seeded.
 *                  Rendered with a "Seed clone" button so the BC can launch
 *                  the pipeline manually (new approvals auto-seed).
 */

export const CLONE_STATUSES = [
  {
    key: 'none',
    label: 'Not Seeded',
    color: 'grey',
    icon: 'circle outline',
    terminal: false,
    virtual: true,
    description:
      'This approved sample has not yet been queued for cloning. Click "Seed" to start the pipeline.'
  },
  {
    key: 'queued',
    label: 'Queued',
    color: 'grey',
    icon: 'clock outline',
    terminal: false,
    description:
      'Waiting for the export worker to pick the sample up and send it to ElevenLabs.'
  },
  {
    key: 'exporting',
    label: 'Exporting',
    color: 'blue',
    icon: 'upload',
    terminal: false,
    description:
      'Uploading the approved WAV to ElevenLabs to create a voice clone.'
  },
  {
    key: 'exported',
    label: 'Exported',
    color: 'teal',
    icon: 'check',
    terminal: false,
    description:
      'ElevenLabs accepted the upload. For IVC the voice is usable immediately; PVC still needs training.'
  },
  {
    key: 'elabs_in_process',
    label: 'Training (PVC)',
    color: 'violet',
    icon: 'cogs',
    terminal: false,
    description:
      'PVC only — ElevenLabs is training the clone. This typically takes several hours.'
  },
  {
    key: 'elabs_completed',
    label: 'ElevenLabs Ready',
    color: 'olive',
    icon: 'check square outline',
    terminal: false,
    description:
      'ElevenLabs reports the voice is ready. The smoke-test TTS will run next to confirm playback.'
  },
  {
    key: 'elabs_failed',
    label: 'ElevenLabs Failed',
    color: 'orange',
    icon: 'ban',
    terminal: true,
    description:
      'ElevenLabs refused the sample (usually audio quality, duration, or policy). Re-record or retry.'
  },
  {
    key: 'smoke_testing',
    label: 'Smoke Testing',
    color: 'blue',
    icon: 'play circle outline',
    terminal: false,
    description:
      'Generating a TTS preview from the cloned voice to confirm it plays correctly.'
  },
  {
    key: 'success',
    label: 'Success',
    color: 'green',
    icon: 'check circle',
    terminal: true,
    description:
      'Voice clone is ready and a smoke-test preview is available. Click ▶ to hear it.'
  },
  {
    key: 'failed',
    label: 'Failed',
    color: 'red',
    icon: 'exclamation circle',
    terminal: true,
    description:
      'An unrecoverable error occurred. Inspect the error message and retry or re-record.'
  },
  {
    key: 'deleting',
    label: 'Deleting',
    color: 'black',
    icon: 'trash',
    terminal: false,
    description:
      'The clone is being removed from ElevenLabs.'
  }
];

export const CLONE_STATUS_MAP = CLONE_STATUSES.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

export const getCloneStatus = (key) =>
  CLONE_STATUS_MAP[key] || CLONE_STATUS_MAP.none;

/**
 * States that still need polling/reconciliation on the client.
 * Terminal states (and the `none` virtual state, which is inert) are excluded
 * so the UI stops hitting the server for them.
 */
export const IN_FLIGHT_STATUSES = CLONE_STATUSES.filter(
  (s) => !s.terminal && !s.virtual
).map((s) => s.key);

/**
 * States the pipeline UI considers "in progress" for the summary stats bar.
 * Terminal states are shown separately (success + failures).
 */
export const SUMMARY_STATUS_KEYS = [
  'queued',
  'exporting',
  'elabs_in_process',
  'success',
  'failed'
];

export default CLONE_STATUSES;
